import React, { useState } from 'react';
import { Card, CardBody, CardTitle, Button, Alert } from 'reactstrap';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faCopy } from '@fortawesome/free-solid-svg-icons';
import ApiKeyCard from '../atoms/ApiKeyCard.js';
import ApiKeyWarning from '../atoms/ApiKeyWarning.js';
import { createApiKey } from '../../services/apiKeyService.js';

const UserApiKeysSection = ({ user, quoteToolClient, onUpdate }) => {
    const [generating, setGenerating] = useState(false);
    const [newApiKey, setNewApiKey] = useState(null);

    const hasApiKeys = user.apiKeyGuids && user.apiKeyGuids.length > 0;
    const isAdmin = user.roles.includes('admin');
    const isDisabled = user.isDisabled;
    const showWarning = hasApiKeys && (isDisabled || !isAdmin);

    const handleGenerateApiKey = async () => {
        try {
            setGenerating(true);
            const response = await createApiKey({ userId: user.id });

            // Display the new API key
            setNewApiKey(response.apiKey);

            // Update user with new API key GUID
            const updatedUser = {
                ...user,
                apiKeyGuids: [...(user.apiKeyGuids || []), response.apiKeyGuid]
            };
            onUpdate(updatedUser);

            toast.success('API key generated successfully');
        } catch (error) {
            console.error('Error generating API key:', error);
            toast.error('Failed to generate API key');
        } finally {
            setGenerating(false);
        }
    };

    const handleRevokeApiKey = (revokedGuid) => {
        const updatedUser = {
            ...user,
            apiKeyGuids: user.apiKeyGuids.filter(guid => guid !== revokedGuid)
        };
        onUpdate(updatedUser);
    };

    const handleCopyNewApiKey = () => {
        if (newApiKey) {
            navigator.clipboard.writeText(newApiKey);
            toast.success('API key copied to clipboard');
        }
    };

    const handleDismissNewKey = () => {
        setNewApiKey(null);
    };

    return (
        <Card>
            <CardBody>
                <CardTitle tag="h5">API Keys</CardTitle>

                {showWarning && <ApiKeyWarning />}

                {newApiKey && (
                    <Alert color="success" className="new-api-key-display" toggle={handleDismissNewKey}>
                        <h6>New API Key Generated</h6>
                        <p className="mb-2">
                            <strong>Important:</strong> Copy this API key now. You won't be able to see it again!
                        </p>
                        <div className="new-api-key-value">
                            {newApiKey}
                        </div>
                        <Button
                            size="sm"
                            color="success"
                            onClick={handleCopyNewApiKey}
                            className="mt-2"
                        >
                            <FontAwesomeIcon icon={faCopy} className="me-2" />
                            Copy to Clipboard
                        </Button>
                    </Alert>
                )}

                <div className="mb-3">
                    <p className="text-muted">
                        API keys allow service-to-service authentication. Only admin users can have API keys.
                    </p>
                </div>

                {isAdmin && (
                    <Button
                        color="primary"
                        onClick={handleGenerateApiKey}
                        disabled={generating}
                        className="mb-3"
                    >
                        <FontAwesomeIcon icon={faPlus} className="me-2" />
                        {generating ? 'Generating...' : 'Generate New API Key'}
                    </Button>
                )}

                {!isAdmin && (
                    <Alert color="info">
                        Only admin users can have API keys. Make the user an admin to generate an API key.
                    </Alert>
                )}

                {hasApiKeys && (isDisabled || !isAdmin) && (
                    <Alert color="danger">
                        <b>WARNING: ALL API KEYS BELOW ARE DISABLED!!!</b> This user is either disabled or no longer an admin. Correct this to enable the API keys.
                    </Alert>
                )}

                {hasApiKeys ? (
                    <div className="api-key-list">
                        <h6>Existing API Keys ({user.apiKeyGuids.length})</h6>
                        {user.apiKeyGuids.map((guid) => (
                            <ApiKeyCard
                                key={guid}
                                apiKeyGuid={guid}
                                userId={user.id}
                                quoteToolClient={quoteToolClient}
                                onRevoke={handleRevokeApiKey}
                            />
                        ))}
                    </div>
                ) : (
                    <p className="text-muted fst-italic">No API keys generated yet.</p>
                )}
            </CardBody>
        </Card>
    );
};

export default UserApiKeysSection;
